"use client";
import Cookies from "js-cookie";
import { createContext, useContext, useEffect, useState } from "react";
import { v4 } from "uuid";
import { SearchSpringCookie } from "../types";

import { useCartContext } from "@/providers/CartContextProvider";

interface CookieAttributes {
	/**
	 * Define when the cookie will be removed. Value can be a Number
	 * which will be interpreted as days from time of creation or a
	 * Date instance. If omitted, the cookie becomes a session cookie.
	 */
	expires?: number | Date | undefined;

	/**
	 * Define the path where the cookie is available. Defaults to '/'
	 */
	path?: string | undefined;

	/**
	 * Define the domain where the cookie is available. Defaults to
	 * the domain of the page where the cookie was created.
	 */
	domain?: string | undefined;

	/**
	 * A Boolean indicating if the cookie transmission requires a
	 * secure protocol (https). Defaults to false.
	 */
	secure?: boolean | undefined;

	/**
	 * Asserts that a cookie must not be sent with cross-origin requests,
	 * providing some protection against cross-site request forgery
	 * attacks (CSRF)
	 */
	sameSite?: "strict" | "Strict" | "lax" | "Lax" | "none" | "None" | undefined;

	/**
	 * An attribute which will be serialized, conformably to RFC 6265
	 * section 5.2.
	 */
	[property: string]: any;
}

interface State {
	ssShopperId: string;
	ssUserId: string;
	ssSessionId: string;
	ssCartProducts: string;
	ssViewedProducts: string;
	handleUpdateViewedProducts: (productSku: string) => void;
}

interface ProviderProps {
	customerId: number | undefined;
	children?: React.ReactNode;
}

export const Context = createContext({} as State);

/**
 * Retrieves a cookie value and sets it if it does not exist.
 *
 * @param {string} cookie - The name of the cookie.
 * @param {CookieAttributes} [options] - Optional cookie attributes.
 * @return {string}
 */
const getCookie = (
	cookie: string,
	options?: {
		value?: string;
		cookie?: CookieAttributes;
	},
) => {
	let value = Cookies.get(cookie);

	if (!value) {
		value = options?.value || v4();
		Cookies.set(cookie, value, options?.cookie);
	}

	return value;
};

export const SearchSpringCookieContextProvider = ({
	customerId,
	children,
}: ProviderProps) => {
	const { cart } = useCartContext();

	const getCartProductCSV = () => {
		if (!cart) {
			return "";
		}

		return cart.line_items.physical_items.map((item) => item.sku).join(",");
	};

	const [ssShopperId] = useState(getCookie(SearchSpringCookie.ssShopperId));

	const [ssUserId] = useState(
		getCookie(SearchSpringCookie.ssUserId, { cookie: { expires: 365 } }),
	);

	const [ssSessionId] = useState(getCookie(SearchSpringCookie.ssSessionId));

	const [ssCartProducts, setSsCartProducts] = useState(
		getCookie(SearchSpringCookie.ssCartProducts, {
			value: getCartProductCSV(),
		}),
	);

	const [ssViewedProducts, setSsViewedProducts] = useState(
		getCookie(SearchSpringCookie.ssViewedProducts, { value: "" }),
	);

	useEffect(() => {
		Cookies.set(SearchSpringCookie.ssShopperId, v4());
	}, []);

	useEffect(() => {
		const updatedValue = getCartProductCSV();

		setSsCartProducts(updatedValue);
		Cookies.set(SearchSpringCookie.ssCartProducts, updatedValue);
	}, [cart]);

	const handleUpdateViewedProducts = (productSku: string) => {
		const viewedProducts = ssViewedProducts
			.split(",")
			.filter((sku) => sku !== productSku);

		viewedProducts.unshift(productSku);

		setSsViewedProducts(viewedProducts.join(","));
		Cookies.set(SearchSpringCookie.ssViewedProducts, viewedProducts.join(","));
		localStorage.setItem("ssViewedProducts", viewedProducts.join(","));
	};

	const value = {
		ssShopperId,
		ssUserId,
		ssSessionId,
		ssCartProducts,
		ssViewedProducts,
		handleUpdateViewedProducts,
	};

	return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useSearchSpringCookieContext = () => {
	const context = useContext(Context);

	if (!context) {
		throw new Error(
			"useSearchSpringCookieContext must be used within a SearchSpringCookieProvider",
		);
	}

	return context;
};

export const SearchSpringLastViewProduct = ({
	productSku,
}: {
	productSku: string;
}) => {
	const { handleUpdateViewedProducts } = useSearchSpringCookieContext();

	useEffect(() => {
		handleUpdateViewedProducts(productSku);
	}, [productSku]);

	return null;
};
